import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, X } from 'lucide-react';
import { AUTH_REQUIRED_MESSAGE } from '../utils/customerAuth';

type AuthRequiredModalProps = {
  open: boolean;
  onClose: () => void;
  message?: string;
  sessionExpired?: boolean;
  returnPath?: string;
};

const AuthRequiredModal: React.FC<AuthRequiredModalProps> = ({
  open,
  onClose,
  message = AUTH_REQUIRED_MESSAGE,
  sessionExpired = false,
  returnPath,
}) => {
  const navigate = useNavigate();

  if (!open) return null;

  const goAuth = (path: '/login' | '/register') => {
    onClose();
    navigate(path, {
      state: {
        from: returnPath || window.location.pathname + window.location.search,
        authMessage: message,
      },
    });
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden
      />
      <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl border border-slate-100">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-orange-50">
          <Lock className="h-7 w-7 text-orange-600" aria-hidden />
        </div>

        <h2 className="text-lg font-bold text-slate-900 text-center">
          {sessionExpired ? 'Session expired' : 'Sign in required'}
        </h2>
        <p className="mt-2 text-sm text-slate-600 text-center leading-relaxed">{message}</p>

        <div className="mt-6 flex flex-col gap-2.5">
          <button
            type="button"
            onClick={() => goAuth('/login')}
            className="w-full rounded-xl bg-slate-900 py-3 text-sm font-semibold text-white hover:bg-slate-800 transition-colors"
          >
            Login
          </button>
          <button
            type="button"
            onClick={() => goAuth('/register')}
            className="w-full rounded-xl border border-slate-200 bg-white py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50 transition-colors"
          >
            Create New Account
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthRequiredModal;
